import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useOutlet } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Menu } from "lucide-react";
import ThemedPage from "src/components/ThemedPage";
import AppSidebar from "src/components/layout/AppSidebar";
import {
  selectIsOnline,
  startPresenceListeners,
  stopPresenceListeners,
} from "src/store/presenceSlice";
import type { AppDispatch } from "src/store";

const SIDEBAR_EXPANDED_KEY = "live_presence_sidebar_expanded";

function readSidebarExpanded(): boolean {
  try {
    const v = localStorage.getItem(SIDEBAR_EXPANDED_KEY);
    if (v === null) return true;
    return v === "true";
  } catch {
    return true;
  }
}

/**
 * Freezes the outlet element for its own pathname so an outgoing route keeps
 * rendering its own content while it fades out. `useOutlet()` always returns
 * the *current* route, so without this freeze the exiting copy would flip to
 * the new page mid-transition.
 */
const FrozenOutlet: React.FC = () => {
  const outlet = useOutlet();
  const [frozen] = useState(outlet);
  return <>{frozen}</>;
};

/**
 * Cross-fades between routes. `mode="wait"` fades the old panel out before the
 * new one fades in; keying by pathname (not full location) means query-param
 * changes don't trigger a re-fade. Route panels still mount/unmount as usual —
 * only the transition is animated.
 */
const AnimatedOutlet: React.FC = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18, ease: "easeInOut" }}
      >
        <FrozenOutlet />
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Authenticated shell: themed background, responsive sidebar (rail / drawer), main outlet.
 */
const AppLayout: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isOnline = useSelector(selectIsOnline);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [railExpanded, setRailExpanded] = useState(() => readSidebarExpanded());
  const location = useLocation();

  const toggleRailExpanded = useCallback(() => {
    setRailExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_EXPANDED_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  useEffect(() => {
    dispatch(startPresenceListeners());
    return () => {
      dispatch(stopPresenceListeners());
    };
  }, [dispatch]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileMenuOpen]);

  return (
    <ThemedPage className="flex h-dvh max-h-dvh min-h-0 flex-col overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row md:items-stretch md:overflow-x-visible md:overflow-y-hidden">
        {/* Tablet + desktop rail — viewport height, does not scroll with main */}
        <aside className="z-20 hidden min-h-0 shrink-0 overflow-x-visible md:flex md:h-full md:max-h-full md:flex-col">
          <AppSidebar
            isOnline={isOnline}
            layout="rail"
            railExpanded={railExpanded}
            onToggleRailExpanded={toggleRailExpanded}
          />
        </aside>

        {/* Mobile drawer */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <>
              <motion.button
                key="mobile-drawer-backdrop"
                type="button"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
                aria-label="Close menu"
                onClick={() => setMobileMenuOpen(false)}
              />
              <motion.div
                key="mobile-drawer-panel"
                id="mobile-navigation"
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{
                  type: "spring",
                  stiffness: 420,
                  damping: 34,
                  mass: 0.85,
                }}
                className="fixed inset-y-0 left-0 z-50 flex w-[min(20rem,88vw)] max-w-full flex-col shadow-2xl md:hidden"
                role="dialog"
                aria-modal="true"
                aria-label="Navigation menu"
              >
                <AppSidebar
                  isOnline={isOnline}
                  layout="drawer"
                  onClose={() => setMobileMenuOpen(false)}
                />
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Mobile: floating menu control only (no app bar) */}
        <button
          type="button"
          onClick={() => setMobileMenuOpen(true)}
          className="fixed left-[max(1rem,env(safe-area-inset-left))] top-[max(1rem,env(safe-area-inset-top))] z-30 flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-gray-950/90 text-gray-100 shadow-lg backdrop-blur-md transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 md:hidden"
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-navigation"
          aria-label="Open navigation menu"
        >
          <Menu className="h-6 w-6" aria-hidden />
        </button>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <main
            id="main-content"
            className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-6 sm:px-6 lg:px-8"
          >
            <AnimatedOutlet />
          </main>
        </div>
      </div>
    </ThemedPage>
  );
};

export default AppLayout;
